(() => {
  const dialog = document.getElementById('ck-search-drawer');
  if (!dialog) return;

  const openBtns = document.querySelectorAll('[data-search-open]');
  const closeBtn = dialog.querySelector('[data-search-close]');
  const clearBtn = dialog.querySelector('[data-search-clear]');
  const input = dialog.querySelector('[data-search-input]');
  const nativeInput = input?.querySelector?.('input') || null;

  const emptyView = dialog.querySelector('[data-search-empty]');
  const resultsView = dialog.querySelector('[data-search-results]');

  const productsRoot = dialog.querySelector('[data-search-products]');
  const pagesRoot = dialog.querySelector('[data-search-pages]');

  const productCountEls = dialog.querySelectorAll('[data-search-product-count]');
  const pageCountEl = dialog.querySelector('[data-search-page-count]');

  const queryLabel = dialog.querySelector('[data-search-query-label]');
  const viewAllLinks = dialog.querySelectorAll('[data-search-view-all]');
  const viewAllPages = dialog.querySelector('[data-search-view-all-pages]');

  let debounceTimer = null;
  let abortController = null;

  const CLOSE_ANIMATION_MS = 200;

  const HIDDEN_CLASSES = ['opacity-0', 'translate-y-2', 'pointer-events-none'];
  const VISIBLE_CLASSES = ['opacity-100', 'translate-y-0', 'pointer-events-auto'];

  function setVisible() {
    dialog.classList.remove(...HIDDEN_CLASSES);
    dialog.classList.add(...VISIBLE_CLASSES);
  }

  function setHidden() {
    dialog.classList.remove(...VISIBLE_CLASSES);
    dialog.classList.add(...HIDDEN_CLASSES);
  }

  function open() {
    dialog.showModal();
    // Animate in on next frame so transitions apply.
    requestAnimationFrame(() => setVisible());
    setTimeout(() => nativeInput?.focus?.(), 40);
    showEmpty();
  }

  function animateClose() {
    if (!dialog.open) return;
    if (dialog.dataset.state === 'closing') return;
    dialog.dataset.state = 'closing';

    // Start close animation (keep modal open until finished).
    setHidden();
    window.setTimeout(() => {
      dialog.close();
    }, CLOSE_ANIMATION_MS);
  }

  function showEmpty() {
    emptyView.classList.remove('hidden');
    resultsView.classList.add('hidden');
  }

  function showResults() {
    emptyView.classList.add('hidden');
    resultsView.classList.remove('hidden');
  }

  function clearResults() {
    productsRoot.innerHTML = '';
    if (pagesRoot) pagesRoot.innerHTML = '';
    productCountEls.forEach((el) => (el.textContent = '0'));
    if (pageCountEl) pageCountEl.textContent = '0';
    if (queryLabel) queryLabel.textContent = '';
  }

  function setQuery(value) {
    // `ck-input` is a web component; set the underlying native input value.
    if (nativeInput) nativeInput.value = value;
    // Some code paths still read `input.value` (host) — keep it in sync if present.
    try {
      input.value = value;
    } catch {
      // ignore
    }
    // Update clear button visibility immediately.
    if (clearBtn) clearBtn.classList.toggle('hidden', !value);
  }

  function handleOpenClick(e) {
    e.preventDefault();

    // If the trigger sits inside another modal (e.g. mobile menu),
    // close that first to avoid competing modal dialogs.
    const trigger = e.currentTarget;
    const modal = trigger?.closest?.('ck-modal');
    const modalClose = modal?.querySelector?.('ck-modal-close');

    if (modalClose) {
      modalClose.click();
      window.setTimeout(open, 0);
      return;
    }

    open();
  }

  openBtns.forEach((btn) => btn.addEventListener('click', handleOpenClick));
  closeBtn?.addEventListener('click', animateClose);
  clearBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    if (abortController) abortController.abort();
    setQuery('');
    clearResults();
    showEmpty();
    nativeInput?.focus?.();
  });

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) animateClose();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && dialog.open) animateClose();
  });

  dialog.addEventListener('close', () => {
    dialog.removeAttribute('data-state');
    // Ensure the next open animates from hidden state.
    setHidden();
    setQuery('');
    if (abortController) abortController.abort();
    clearResults();
    showEmpty();
  });

  dialog.querySelectorAll('[data-search-term]').forEach((el) => {
    el.addEventListener('click', (e) => {
      // Only intercept elements that are meant to behave like “search term chips”.
      // Never block normal anchor navigation (marketing/quick links).
      if (el.tagName === 'A' && el.hasAttribute('href')) return;

      e.preventDefault();
      setQuery(el.dataset.searchTerm || '');
      nativeInput?.dispatchEvent(new Event('input', { bubbles: true }));
      nativeInput?.focus?.();
    });
  });

  function getQueryFromInputEvent(e) {
    // `ck-input` may not expose a string `.value` consistently; avoid calling `.trim()` on undefined.
    const eventValue = e?.target?.value;
    const raw = eventValue ?? input?.value;
    if (raw == null) return '';
    if (typeof raw === 'string') return raw.trim();
    return String(raw).trim();
  }

  input.addEventListener('input', (e) => {
    const q = getQueryFromInputEvent(e);
    if (clearBtn) clearBtn.classList.toggle('hidden', !q);

    if (!q) {
      if (abortController) abortController.abort();
      clearResults();
      showEmpty();
      return;
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runPredictive(q), 180);
  });

  async function runPredictive(q) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const isB2B = document.documentElement.getAttribute('data-theme') === 'b2b';
    const predictiveSectionId = isB2B ? 'search-drawer-predictive-b2b' : 'search-drawer-predictive';

    // Update “view all” links and headings
    let searchUrl = `/search?q=${encodeURIComponent(q)}`;
    if (typeof window.__addB2CFilterToSearchUrl === 'function') {
      searchUrl = window.__addB2CFilterToSearchUrl(searchUrl);
    }
    viewAllLinks.forEach((link) => {
      link.href = searchUrl;
    });
    if (viewAllPages) viewAllPages.href = searchUrl;
    if (queryLabel) queryLabel.textContent = q;

    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,page',
      // Keep predictive results intentionally small; “View all” shows full results page.
      'resources[limit]': '3',
      'resources[limit_scope]': 'each',
      section_id: predictiveSectionId,
    });

    // Show loading state in search results area
    let loadingSpinner = productsRoot?.querySelector('ck-spinner[data-search-loading]');
    if (!loadingSpinner && productsRoot) {
      loadingSpinner = document.createElement('ck-spinner');
      loadingSpinner.setAttribute('data-search-loading', 'true');
      loadingSpinner.setAttribute('size', 'md');
      loadingSpinner.className = 'mx-auto my-8';
      productsRoot.innerHTML = '';
      productsRoot.appendChild(loadingSpinner);
    }

    try {
      const res = await fetch(`/search/suggest?${params.toString()}`, {
        signal: abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error('Predictive search failed');
      const html = await res.text();
      render(q, html);
    } catch (err) {
      if (err.name === 'AbortError') {
        if (loadingSpinner && loadingSpinner.parentNode) {
          loadingSpinner.remove();
        }
        return;
      }
      console.error(err);
      if (loadingSpinner && loadingSpinner.parentNode) {
        loadingSpinner.remove();
      }
    }
  }

  function render(q, html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('[data-search-predictive]');
    if (!root) {
      // If the response doesn't include our section markup, fail gracefully.
      clearResults();
      showResults();
      return;
    }

    const productHtml = root.querySelector('[data-search-products]')?.innerHTML || '';
    const pagesHtml = root.querySelector('[data-search-pages]')?.innerHTML || '';

    productsRoot.innerHTML = productHtml;
    if (pagesRoot) pagesRoot.innerHTML = pagesHtml;

    const productCount = root.getAttribute('data-product-count') || '0';
    const pageCount = root.getAttribute('data-page-count') || '0';

    productCountEls.forEach((el) => (el.textContent = productCount));
    if (pageCountEl) pageCountEl.textContent = pageCount;

    showResults();
  }
})();
